// useWalletPortfolio — holdings + PnL summary for ONE wallet address.
//
// Bootstrap: GET /v1/wallets/:address/portfolio (summary and holdings in one
// answer). Realtime: a swap moves the wallet's native balance, so the
// wallet:{address} balance push doubles as "your positions just changed".
//
// The push carries a balance, not positions, so it cannot be merged into the
// holdings list — it can only invalidate it. Reloads are therefore bounded and
// coalesced: a burst restarts one short convergence sequence, while delayed
// projector writes are still picked up without waiting for another WS event.
// The authoritative value is always the refetch.

import { useCallback, useEffect, useRef, useState } from 'react'
import { type WalletPortfolio, fetchWalletPortfolio } from '../api/portfolio'
import { displayDiagEnabled, noteDisplayEvent } from '../lib/displayWriteDiag'
import { rememberTokenFaces } from '../lib/holdingRows'
import { jettonsChannel } from '../lib/journalFrames'
import { applyJettonFrame, isJournalPortfolio, mergePortfolio } from '../lib/portfolioPush'
import { canonicalTonAddress } from '../lib/tonAddress'
import { useWsOptional } from '../ws/WsContext'
import type { PortfolioSeed } from './usePortfolioSeed'
import { useWsReseed } from './useWsReseed'
import { useWsSubscription } from './useWsSubscription'

// The wallet-balance push can beat the canonical trade projector. Re-read a
// small bounded number of times so a fast first response cannot leave holdings
// stale until a manual refresh. A burst resets the sequence, so one swap still
// produces at most three reads rather than one read per internal message.
const RELOAD_DELAYS_MS = [0, 300, 900, 2000] as const

const rememberedPortfolios = new Map<string, WalletPortfolio>()
const PORTFOLIO_STORE = 'wt:portfolio:'

function storeKey(address: string): string {
  return PORTFOLIO_STORE + (canonicalTonAddress(address) ?? address)
}

function readStoredPortfolio(address: string): WalletPortfolio | null {
  try {
    const raw = localStorage.getItem(storeKey(address))
    return raw ? (JSON.parse(raw) as WalletPortfolio) : null
  } catch {
    return null
  }
}

function writeStoredPortfolio(address: string, portfolio: WalletPortfolio): void {
  try {
    localStorage.setItem(storeKey(address), JSON.stringify(portfolio))
  } catch {
    /* quota / private mode */
  }
}

function recallPortfolio(address: string): WalletPortfolio | null {
  const key = canonicalTonAddress(address) ?? address
  return rememberedPortfolios.get(key) ?? readStoredPortfolio(address)
}

export function resetWalletPortfolioMemoryForTest(): void {
  rememberedPortfolios.clear()
  try {
    for (const key of Object.keys(localStorage)) {
      if (key.startsWith(PORTFOLIO_STORE)) localStorage.removeItem(key)
    }
  } catch {
    /* jsdom */
  }
}

export type PortfolioStatus = 'loading' | 'ready' | 'offline'

export type UseWalletPortfolio = {
  status: PortfolioStatus
  portfolio: WalletPortfolio | null
  /**
   * Wall-clock ms at which the request behind the CURRENT portfolio was issued.
   *
   * Every price in that answer was read at or after this moment and never
   * after it arrived — so this, not the arrival time, is how fresh canon's
   * knowledge is. The shared price store needs it: a read issued before the
   * swap reached QuestDB comes back after the tape has already delivered the
   * tick, and without this it would overwrite the new price with the old one.
   */
  requestedAtMs: number | null
  reload: () => void
}

export function useWalletPortfolio(
  address: string | null,
  seed?: PortfolioSeed | null
): UseWalletPortfolio {
  const canonicalAddress = canonicalTonAddress(address)
  const [status, setStatus] = useState<PortfolioStatus>(() =>
    address && recallPortfolio(address) ? 'ready' : 'loading'
  )
  const [portfolio, setPortfolio] = useState<WalletPortfolio | null>(() => {
    if (!address) return null
    const remembered = recallPortfolio(address)
    if (remembered) rememberTokenFaces(remembered.holdings)
    return remembered
  })
  // Set together with the portfolio it belongs to, never separately: the pair
  // is the claim "this answer is current to this moment", and half of it is
  // worse than neither.
  const [requestedAtMs, setRequestedAtMs] = useState<number | null>(null)
  const alive = useRef(true)
  const timers = useRef<ReturnType<typeof setTimeout>[]>([])
  const latestEventID = useRef(-1)
  const inFlight = useRef(new Set<string>())
  const pendingLoad = useRef(new Set<string>())
  // The address this hook is currently showing. A response that resolves after
  // the user switched wallets belongs to the previous address and must be
  // dropped: otherwise a slow fetch for A lands on top of B's portfolio and
  // shows one wallet's holdings under another's name.
  const wanted = useRef<string | null>(address)

  // Seed mode: the page composed the bootstrap into ONE /seed fetch, so this
  // hook skips its own initial /portfolio read and applies the seed's
  // portfolio section instead. Reloads (realtime, manual) keep using the
  // standalone endpoint, unchanged. `undefined` = legacy standalone mode
  // (other callers); null = seed mode, seed still pending (a wallet switch
  // renders once with the previous seed state before the new one loads).
  const seeded = seed !== undefined

  // apply funnels every portfolio answer — standalone fetch or seed section —
  // through the same liveness/ownership/checkpoint guards.
  const apply = useCallback((addr: string, p: WalletPortfolio | null, issuedAtMs: number): void => {
    if (!alive.current || wanted.current !== addr) return
    // fetchWalletPortfolio maps gate-off / unreachable to null.
    if (p === null) {
      setStatus('offline')
      return
    }
    // Never let an older projection response replace a newer checkpoint.
    if (p.updated_through_wallet_event_id < latestEventID.current) {
      if (displayDiagEnabled())
        noteDisplayEvent(
          'portfolio',
          'answer',
          `evt=${p.updated_through_wallet_event_id}`,
          'canon',
          'refused-stale-checkpoint'
        )
      return
    }
    latestEventID.current = p.updated_through_wallet_event_id
    rememberedPortfolios.set(canonicalTonAddress(addr) ?? addr, p)
    writeStoredPortfolio(addr, p)
    rememberTokenFaces(p.holdings)
    if (displayDiagEnabled())
      noteDisplayEvent(
        'portfolio',
        'answer',
        `evt=${p.updated_through_wallet_event_id} rows=${p.holdings.length}`,
        'canon',
        'applied'
      )
    // NB: do not compare p.wallet_address to addr — the backend normalizes
    // (a UQ-form address in the URL echoes back as EQ), so that check would
    // drop every response for a non-canonical address form.
    // Merged in place (lib/portfolioPush): an unchanged holding keeps its
    // object, an unchanged answer keeps the portfolio itself, and a balance
    // pushed at a newer journal version than this read is kept.
    setPortfolio((prev) => mergePortfolio(prev, p))
    setRequestedAtMs(issuedAtMs)
    setStatus('ready')
  }, [])

  const load = useCallback(
    (addr: string): void => {
      if (inFlight.current.has(addr)) {
        pendingLoad.current.add(addr)
        return
      }
      inFlight.current.add(addr)
      // Stamped BEFORE the request goes out. The answer's prices were read at
      // or after this moment, never later than it arrived, so this is the
      // honest bound on how fresh canon's knowledge is.
      const issuedAtMs = Date.now()
      void fetchWalletPortfolio(addr)
        .then((p) => apply(addr, p, issuedAtMs))
        .finally(() => {
          inFlight.current.delete(addr)
          const rerun = pendingLoad.current.delete(addr)
          if (rerun && alive.current && wanted.current === addr) load(addr)
        })
    },
    [apply]
  )

  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
      for (const timer of timers.current) clearTimeout(timer)
      timers.current = []
      pendingLoad.current.clear()
    }
  }, [])

  useEffect(() => {
    wanted.current = address
    latestEventID.current = -1
    pendingLoad.current.clear()
    // A reload queued for the previous address must not fire against the new one.
    for (const timer of timers.current) clearTimeout(timer)
    timers.current = []
    if (!address) {
      setPortfolio(null)
      setRequestedAtMs(null)
      setStatus('ready')
      return
    }
    const remembered = recallPortfolio(address)
    if (remembered) {
      rememberTokenFaces(remembered.holdings)
      setPortfolio(remembered)
      setStatus('ready')
    } else {
      setStatus('loading')
      setPortfolio(null)
      setRequestedAtMs(null)
    }
    // Seed mode: the composed /seed fetch is already in flight; the effect
    // below applies its portfolio section. A stale seed from the previous
    // address still counts as seed mode — the new seed is coming.
    if (seeded) return
    load(address)
  }, [address, load, seeded])

  // Applies the composed seed's portfolio section through the same guards as
  // a standalone response. A seed for another address (previous wallet) is
  // ignored; the current wallet's seed arrives as a new object.
  useEffect(() => {
    if (!seed || seed.status !== 'done' || !address || seed.address !== address) return
    // The seed carries when ITS request went out, on the same clock as a
    // standalone read, so a seed answering slowly cannot overwrite a tick.
    apply(address, seed.portfolio, seed.requestedAtMs)
  }, [seed, address, apply])

  const scheduleReload = useCallback(
    (addr: string) => {
      for (const timer of timers.current) clearTimeout(timer)
      timers.current = RELOAD_DELAYS_MS.map((delay) => setTimeout(() => load(addr), delay))
    },
    [load]
  )

  useWsSubscription(canonicalAddress ? `wallet:${canonicalAddress}` : null, () => {
    if (address) scheduleReload(address)
  })

  // Journal mode: each token balance is pushed per settlement on
  // jettons:<owner> and moves its holding the instant it lands, in place
  // (lib/portfolioPush). A token the portfolio has no row for is read again.
  // Subscribed only while the portfolio reads the journal; on (re)subscribe
  // the portfolio is read again so no settlement before it is lost.
  const journal = isJournalPortfolio(portfolio)
  const jettons = journal ? jettonsChannel(canonicalAddress) : null
  const shownRef = useRef(portfolio)
  shownRef.current = portfolio
  useWsSubscription(jettons, (msg) => {
    if (!address || wanted.current !== address) return
    const shown = shownRef.current
    if (shown != null && applyJettonFrame(shown, address, msg) === 'reread') {
      load(address)
      return
    }
    setPortfolio((prev) => {
      if (prev == null) return prev
      const next = applyJettonFrame(prev, address, msg)
      return next === 'reread' ? prev : next
    })
  })
  const ws = useWsOptional()
  useEffect(() => {
    if (!ws || !jettons || !address) return
    let live = true
    ws.waitForSubscription(jettons).then(
      () => {
        if (live && wanted.current === address) load(address)
      },
      () => {}
    )
    return () => {
      live = false
    }
  }, [ws, jettons, address, load])

  // A push published while the socket was down is gone forever: the gateway
  // replays SUBSCRIPTIONS on reopen, not the events missed in the gap. The
  // balance lane survives that on its own heartbeat; this one had nothing, so
  // a trade that confirmed during a reconnect left the position stale until
  // the next trade. Every other REST-seeded feed in the app already does this
  // (useBoardFeed, useDiscoverFeed) — the portfolio was the omission.
  useWsReseed(() => {
    if (address) load(address)
  })

  // Cheap insurance on top of the live lane: coming back to the tab re-reads
  // canon. It is not what keeps the numbers moving — prices do that, from the
  // tape — it is what catches whatever the tape could not say, including a
  // token nobody traded while the tab was hidden.
  useEffect(() => {
    if (!address) return
    const onVisible = () => {
      if (document.visibilityState === 'visible') load(address)
    }
    document.addEventListener('visibilitychange', onVisible)
    // pageshow covers the bfcache restore, which fires no visibilitychange at
    // all (the same gap WsContext documents for the socket).
    window.addEventListener('pageshow', onVisible)
    return () => {
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('pageshow', onVisible)
    }
  }, [address, load])

  return {
    status,
    portfolio,
    requestedAtMs,
    reload: useCallback(() => {
      if (address) load(address)
    }, [address, load]),
  }
}
